import _ from 'lodash-es';
import kformUtils from '~/components/kform/utils/kform-utils';

/** @type {any} */
const additionalShortcuts = {
	button: [
		'Enter',
		'Esc',
	],
};

/**
 * @param {string} type
 * @returns {*} Additional Shortcuts
 */
const getAdditionalShortcuts = type => additionalShortcuts[type] || [];

const getAlphaShortcuts = () => {
	const start = 'A'.codePointAt(0);
	const end = 'Z'.codePointAt(0);
	return start !== undefined && end !== undefined ? _.range(start, end + 1)
		.map(charCode => String.fromCodePoint(charCode)) : [];
};

/**
 * Memoize the given form components in a map, using the component keys.
 *
 * @param {any[]} components
 *   An array of the form components.
 * @param {any} input
 *   The input component we're trying to uniquify.
 * @returns {any}
 *   The memoized form components.
 */
const findExistingComponents = (components, input) => {
	// Prebuild a list of existing components.
	/** @type {any} */
	const existingComponents = {};
	kformUtils.eachComponent(components, (/** @type {any} */ component) => {
		// If theres no key, we cant compare components.
		if (!component.key) {
			return;
		}

		// A component is pre-existing if the key is unique, or the key is a duplicate and
		// its not flagged as the new component.
		if (
			(component.key !== input.key)
			|| ((component.key === input.key) && (!!component.isNew !== !!input.isNew))
		) {
			existingComponents[component.key] = component;
		}
	}, true);

	return existingComponents;
};

/**
 * @param {*} components
 * @param {*} input
 * @returns {*} Returns binded shortcuts
 */
const getBindedShortcuts = (components, input) => {
	/**
	 * @type {any[]}
	 */
	const result = [];

	kformUtils.eachComponent(components, (/** @type {any} */ component) => {
		if (component === input) {
			return;
		}

		if (component.shortcut) {
			result.push(component.shortcut);
		}
		if (component.values) {
			for (const value of component.values) {
				if (value.shortcut) {
					result.push(value.shortcut);
				}
			}
		}
	}, true);

	return result;
};

/**
 * @param {*} form
 * @param {*} component
 * @returns {*} Returns available shortcuts
 */
const getAvailableShortcuts = (form, component) => _.difference(
	[...getAlphaShortcuts(), ...getAdditionalShortcuts(component.type)],
	getBindedShortcuts(form.components, component),
);

/**
 * Determine if the given component key already exists in the memoization.
 *
 * @param {any} memoization The form components map.
 * @param {string} key
 * @returns {boolean} Whether or not the key exists.
 */
const keyExists = (memoization, key) => Object.prototype.hasOwnProperty.call(memoization, key);

const suffixRegex = /(\d+)$/;

/**
 * Iterate the given key to make it unique.
 *
 * @param {*} key Modify the component key to be unique.
 * @returns {string} The new component key.
 */
const iterateKey = key => {
	if (!suffixRegex.test(key)) {
		return `${key }2`;
	}

	return key.replace(suffixRegex, (/** @type {any} */ suffix) => Number(suffix) + 1);
};

/**
 * Appends a number to a component.key to keep it unique
 *
 * @param {any} form The components parent form.
 * @param {any} component The component to uniquify
 * @returns {any} Uniquified component
 */
const uniquify = (form, component) => {
	const isNew = component.isNew || false;

	// Recurse into all child components.
	kformUtils.eachComponent([component], (/** @type {any} */ component) => {
		// Force the component isNew to be the same as the parent.
		component.isNew = isNew;

		// Skip key uniquification if this component doesn't have a key.
		if (!component.key) {
			return;
		}

		const memoization = findExistingComponents(form.components, component);
		while (keyExists(memoization, component.key)) {
			component.key = iterateKey(component.key);
		}
	}, true);
	return component;
};

export default {
	getAvailableShortcuts,
	uniquify,
};
